"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../components/Layout"

const Adding = ({ onSuccess }) => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "",
    dosage: "",
    unit: "mg", // Default unit
    country: "",
    expiryDate: "",
    Form: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [apiError, setApiError] = useState(null);
  const API_BASE_URL = 'https://api.tenarx.net';

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (apiError) setApiError(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Medicine Name is required";
    if (!formData.dosage.trim()) newErrors.dosage = "Dosage is required";
    else if (isNaN(formData.dosage))
      newErrors.dosage = "Dosage must be a number";
    if (!formData.unit.trim()) newErrors.unit = "Unit is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.expiryDate.trim())
      newErrors.expiryDate = "Expiry date is required";
    if (!formData.Form.trim()) newErrors.Form = "Form is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError(null);
    setSuccessMessage("");

    try {
      const payload = {
        name: formData.name,
        content: formData.dosage,
        country: formData.country,
        expireDate: formData.expiryDate,
        type: formData.Form,
        measurementUnit: formData.unit,
        
      };

      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${API_BASE_URL}/api/medicines/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to add medicine");
      }

      setSuccessMessage("Medicine added successfully!");

      if (onSuccess) onSuccess(data);

      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: "",
          dosage: "",
          unit: "mg",
          country: "",
          expiryDate: "",
          Form: "",
        });
        setSuccessMessage("");
      }, 2000);
    } catch (error) {
      console.error("API Error:", error);
      const errorMessage = error.message || "Network request failed";
      setApiError(errorMessage);
      // For web, we're showing the error in the form instead of Alert
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ADD MEDICINE</h1>
          <p className="text-gray-600">Please fill all required information</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Medicine Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Medicine Name *</label>
              {errors.name && <p className="text-red-500 text-sm mb-2">{errors.name}</p>}
              <input
                type="text"
                placeholder="Medicine Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.name ? "border-red-300" : "border-gray-300"
                } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
              />
            </div>

            {/* Dosage and Unit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Dosage *</label>
              {errors.dosage && <p className="text-red-500 text-sm mb-2">{errors.dosage}</p>}
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Dosage"
                  value={formData.dosage}
                  onChange={(e) => handleChange("dosage", e.target.value)}
                  disabled={isSubmitting}
                  className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.dosage ? "border-red-300" : "border-gray-300"
                  } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
                />
                <select
                  value={formData.unit}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  disabled={isSubmitting}
                  className={`w-32 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.unit ? "border-red-300" : "border-gray-300"
                  } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
                >
                  <option value="mg">mg</option>
                  <option value="mcg">mcg</option>
                  <option value="L">L</option>
                  <option value="g">g</option>
                  <option value="ml">ml</option>
                  <option value="IU">IU</option>
                  <option value="%">%</option>

                </select>
              </div>
              {errors.unit && <p className="text-red-500 text-sm mt-2">{errors.unit}</p>}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Country *</label>
              {errors.country && <p className="text-red-500 text-sm mb-2">{errors.country}</p>}
              <input
                type="text"
                placeholder="Country"
                value={formData.country}
                onChange={(e) => handleChange("country", e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.country ? "border-red-300" : "border-gray-300"
                } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date *</label>
              {errors.expiryDate && <p className="text-red-500 text-sm mb-2">{errors.expiryDate}</p>}
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => handleChange("expiryDate", e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.expiryDate ? "border-red-300" : "border-gray-300"
                } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
              />
            </div>

            {/* Form */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Form *</label>
              {errors.Form && <p className="text-red-500 text-sm mb-2">{errors.Form}</p>}
              <select
                value={formData.Form}
                onChange={(e) => handleChange("Form", e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.Form ? "border-red-300" : "border-gray-300"
                } ${isSubmitting ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
              >
                <option value="">Select Form</option>
                <option value="tablet">Tablet</option>
                <option value="capsule">Capsule</option>
                <option value="syrup">Syrup</option>
                <option value="injection">Injection</option>
                <option value="cream">Cream</option>
                <option value="ointment">Ointment</option>
                <option value="drops">Drops</option>
                <option value="powder">Powder</option>
                <option value="inhaler">Inhaler</option>
                <option value="patch">Patch</option>
                <option value="liquid">Liquid</option>
                <option value="lozenge">Lozenge</option>
                <option value="nasal spray">Nasal Spray</option>

              </select>
            </div>

            {/* API Error Message */}
            {apiError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{apiError}</p>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">{successMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5"
                }`}
              >
                {isSubmitting ? "SUBMITTING..." : "ADD MEDICINE"}
              </button>
            </div>

            {/* Cancel Button */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/medicine")}
                disabled={isSubmitting}
                className="text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancel and go back
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  )
}

export default Adding